// A2 — Estructura física: Plantas → Áreas → Líneas.
// El Admin crea/edita la estructura; el Coordinador la usa pero no la modifica.
// Desactivar exige razón y se bloquea si hay operación viva (lo valida el backend).

import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import {
  useAdminEstructura,
  useCreateAdminPlanta,
  useUpdateAdminPlanta,
  useCreateAdminArea,
  useUpdateAdminArea,
  useCreateAdminLinea,
  useUpdateAdminLinea,
} from '../../lib/api/queries';

type Nivel = 'planta' | 'area' | 'linea';
type EditCtx = { nivel: Nivel; id: string; nombre: string; codigo: string; activa: boolean };
type CrearCtx =
  | { nivel: 'planta' }
  | { nivel: 'area'; plantaId: string }
  | { nivel: 'linea'; plantaId: string; areas: Array<{ id: string; nombre: string }> };

export function A2EstructuraPage() {
  const { data, isLoading, isError } = useAdminEstructura();
  const [crear, setCrear] = useState<CrearCtx | null>(null);
  const [editar, setEditar] = useState<EditCtx | null>(null);

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Plantas · Áreas · Líneas</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">Estructura física del tenant</p>
          </div>
          <Button onClick={() => setCrear({ nivel: 'planta' })}>Nueva planta</Button>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {data.plantas.map((p) => (
              <Card
                key={p.id}
                variant={p.activa ? 'default' : 'default'}
                className={p.activa ? '' : 'opacity-60'}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-fg">{p.nombre}</span>
                      <span className="text-[11px] text-fg-tertiary">{p.codigo}</span>
                      {p.direccion ? (
                        <span className="text-[11px] text-fg-tertiary">· {p.direccion}</span>
                      ) : null}
                      {!p.activa && <Badge tone="neutral">Inactiva</Badge>}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCrear({ nivel: 'area', plantaId: p.id })}
                      >
                        + Área
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setEditar({
                            nivel: 'planta',
                            id: p.id,
                            nombre: p.nombre,
                            codigo: p.codigo,
                            activa: p.activa,
                          })
                        }
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
                    {p.areas.map((a) => (
                      <div key={a.id} className="rounded border border-border p-2">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-fg">{a.nombre}</span>
                            <span className="text-[10px] text-fg-tertiary">{a.codigo}</span>
                            {!a.activa && <Badge tone="neutral">Inactiva</Badge>}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setCrear({
                                  nivel: 'linea',
                                  plantaId: p.id,
                                  areas: p.areas.map((ar) => ({ id: ar.id, nombre: ar.nombre })),
                                })
                              }
                            >
                              + Línea
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setEditar({
                                  nivel: 'area',
                                  id: a.id,
                                  nombre: a.nombre,
                                  codigo: a.codigo,
                                  activa: a.activa,
                                })
                              }
                            >
                              Editar
                            </Button>
                          </div>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5 pl-2">
                          {a.lineas.map((l) => (
                            <button
                              key={l.id}
                              type="button"
                              onClick={() =>
                                setEditar({
                                  nivel: 'linea',
                                  id: l.id,
                                  nombre: l.nombre,
                                  codigo: l.codigo,
                                  activa: l.activa,
                                })
                              }
                              className={`rounded border px-2 py-0.5 text-[11px] ${l.activa ? 'border-border text-fg' : 'border-border text-fg-tertiary line-through'}`}
                            >
                              {l.codigo}
                            </button>
                          ))}
                          {a.lineas.length === 0 && (
                            <span className="text-[11px] text-fg-tertiary">sin líneas</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {p.areas.length === 0 && (
                      <span className="text-xs text-fg-tertiary">Sin áreas</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      {crear && <CrearDialog ctx={crear} onClose={() => setCrear(null)} />}
      {editar && <EditarDialog ctx={editar} onClose={() => setEditar(null)} />}
    </RoleLayout>
  );
}

function CrearDialog({ ctx, onClose }: { ctx: CrearCtx; onClose: () => void }) {
  const crearPlanta = useCreateAdminPlanta();
  const crearArea = useCreateAdminArea();
  const crearLinea = useCreateAdminLinea();
  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState('');
  const [areaId, setAreaId] = useState(ctx.nivel === 'linea' ? (ctx.areas[0]?.id ?? '') : '');
  const titulo =
    ctx.nivel === 'planta' ? 'Nueva planta' : ctx.nivel === 'area' ? 'Nueva área' : 'Nueva línea';
  const pending = crearPlanta.isPending || crearArea.isPending || crearLinea.isPending;

  function submit() {
    const onSuccess = () => {
      toast.success(`${titulo} creada`);
      onClose();
    };
    const onError = (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error');
    if (ctx.nivel === 'planta')
      crearPlanta.mutate({ nombre: nombre.trim(), codigo: codigo.trim() }, { onSuccess, onError });
    else if (ctx.nivel === 'area')
      crearArea.mutate(
        { plantaId: ctx.plantaId, body: { nombre: nombre.trim(), codigo: codigo.trim() } },
        { onSuccess, onError },
      );
    else
      crearLinea.mutate(
        {
          plantaId: ctx.plantaId,
          body: { area_id: areaId, nombre: nombre.trim(), codigo: codigo.trim() },
        },
        { onSuccess, onError },
      );
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {ctx.nivel === 'linea' && (
            <div className="flex flex-col gap-1">
              <Label>Área</Label>
              <Select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                {ctx.areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nombre}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Label>Nombre</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Código</Label>
            <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={
              pending || !nombre.trim() || !codigo.trim() || (ctx.nivel === 'linea' && !areaId)
            }
            onClick={submit}
          >
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditarDialog({ ctx, onClose }: { ctx: EditCtx; onClose: () => void }) {
  const upPlanta = useUpdateAdminPlanta();
  const upArea = useUpdateAdminArea();
  const upLinea = useUpdateAdminLinea();
  const [f, setF] = useState({
    nombre: ctx.nombre,
    codigo: ctx.codigo,
    activa: ctx.activa,
    razon: '',
  });
  const desactivando = ctx.activa && !f.activa;
  const pending = upPlanta.isPending || upArea.isPending || upLinea.isPending;

  function submit() {
    const body = {
      nombre: f.nombre.trim(),
      codigo: f.codigo.trim(),
      activa: f.activa,
      ...(desactivando ? { razon: f.razon.trim() } : {}),
    };
    const opts = {
      onSuccess: () => {
        toast.success('Guardado');
        onClose();
      },
      onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
    };
    if (ctx.nivel === 'planta') upPlanta.mutate({ id: ctx.id, body }, opts);
    else if (ctx.nivel === 'area') upArea.mutate({ id: ctx.id, body }, opts);
    else upLinea.mutate({ id: ctx.id, body }, opts);
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar {ctx.nivel}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label>Nombre</Label>
            <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Código</Label>
            <Input value={f.codigo} onChange={(e) => setF({ ...f, codigo: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.activa}
              onChange={(e) => setF({ ...f, activa: e.target.checked })}
            />
            Activa
          </label>
          {desactivando && (
            <div className="flex flex-col gap-1">
              <Label>Razón de desactivación (≥20)</Label>
              <Input value={f.razon} onChange={(e) => setF({ ...f, razon: e.target.value })} />
              <span className="text-[10px] text-fg-tertiary">
                Se bloquea si la unidad tiene programación vigente o producción reciente.
              </span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={
              pending ||
              !f.nombre.trim() ||
              !f.codigo.trim() ||
              (desactivando && f.razon.trim().length < 20)
            }
            onClick={submit}
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
